import type { IncidentGroup } from '../../services/incidentsApi';
import type { IncidentTimeRange } from '../../pages/incidents/useIncidentsTimeRange';

type PatternActionPageTimeRange = Pick<IncidentTimeRange, 'fromDate' | 'toDate'>;

export type GroupPatternActionTimeRangeSource = 'group_bounds' | 'page_filter';

export type ResolvedGroupPatternActionTimeRange = PatternActionPageTimeRange & {
  source: GroupPatternActionTimeRangeSource;
};

/** Maximum review window span for group pattern actions (safety cap). */
export const GROUP_PATTERN_ACTION_MAX_WINDOW_MS = 90 * 24 * 60 * 60 * 1000;

function parseInstant(value: string | null | undefined): Date | null {
  const text = String(value || '').trim();
  if (!text) return null;
  const ms = Date.parse(text);
  if (!Number.isFinite(ms)) return null;
  return new Date(ms);
}

function pickGroupBound(...candidates: Array<string | null | undefined>): Date | null {
  for (const candidate of candidates) {
    const parsed = parseInstant(candidate);
    if (parsed) return parsed;
  }
  return null;
}

/**
 * EPIC 97 — default risk-owner / group-action window from recurring-risk occurrence span.
 * Falls back to the page filter when group bounds are missing.
 */
export function resolveGroupPatternActionTimeRange(
  group: IncidentGroup | null | undefined,
  pageTimeRange: PatternActionPageTimeRange,
): ResolvedGroupPatternActionTimeRange {
  const pageFrom = String(pageTimeRange.fromDate || '').trim();
  const pageTo = String(pageTimeRange.toDate || '').trim();

  const first = pickGroupBound(group?.first_seen, group?.started_at);
  const last = pickGroupBound(group?.last_seen, group?.last_seen_at);

  if (!first || !last) {
    return {
      fromDate: pageFrom || undefined,
      toDate: pageTo || undefined,
      source: 'page_filter',
    };
  }

  let from = first.getTime() <= last.getTime() ? first : last;
  let to = first.getTime() <= last.getTime() ? last : first;

  const spanMs = to.getTime() - from.getTime();
  if (spanMs > GROUP_PATTERN_ACTION_MAX_WINDOW_MS) {
    from = new Date(to.getTime() - GROUP_PATTERN_ACTION_MAX_WINDOW_MS);
  }

  return {
    fromDate: from.toISOString(),
    toDate: to.toISOString(),
    source: 'group_bounds',
  };
}

export function groupPatternActionWindowHelper(source: GroupPatternActionTimeRangeSource): string {
  if (source === 'group_bounds') {
    return 'Review window defaults to this recurring risk’s first through last occurrence in the queue. Open linked records in that window receive the risk owner.';
  }
  return 'Review window uses the page time filter. Adjust the incidents list dates if you need a wider backfill.';
}
